import {Inject, Injectable} from "@angular/core";
import {FormService, ValidationMsg} from "../../../../umbrella-common/services/form.service";
import {FormBuilder, FormGroup} from "@angular/forms";
import {Order, Product, Scope} from "../../../../store/state/order.state";
import {AppState} from "../../../../store/state/app.state";
import {Store} from "@ngrx/store";
import {BaseOrderFormService} from "../../common/base-order-form.service";
import {List} from 'immutable';
import {COMPANY_GENERAL_INFO_FORM_CONTROL_HANDLERS, FormControlHandler} from "./form-control.handler";
import {TranslateService} from "@ngx-translate/core";

@Injectable()
export class CompanyGeneralInfoFormService extends FormService implements BaseOrderFormService {

	constructor(@Inject(COMPANY_GENERAL_INFO_FORM_CONTROL_HANDLERS) private formControlHandlers: FormControlHandler[], translate: TranslateService) {
		super(translate);
	}

	public static readonly MAX_NUMBER_OF_EMPLOYEES = 2025;

	readonly FORM_CONTROL_ERROR_MSG_MAP: { [validationKey: string]: ValidationMsg[] } = {
		employeeWithinScopeToHigh: [{msg: 'validations.company-general-info.employee-within-scope-to-high'}],
		employeeWithinScopeToLow: [{msg: 'validations.company-general-info.employee-within-scope-to-low'}],
		minlength: [
			{msg: 'validations.minlength'},
			{formControlName: 'companyDescription', msg: 'validations.minlength'}
		],
		required: [
			{msg: 'validations.required'},
			{formControlName: 'companyScopes', msg: 'validations.company-general-info.company-scopes-required'},
			{formControlName: 'companyDescription', msg: 'validations.company-general-info.company-description-required'},
		],
		pattern: [
			{formControlName: 'employeesCount', msg: 'validations.company-general-info.employees-count-pattern'},
			{formControlName: 'shiftsNumberMore', msg: 'validations.company-general-info.shifts-number-more'}
		],
		radiobuttonRequired: [
			{msg: 'validations.radiobuttonRequired'}
		],
		max: [
			{msg: 'validations.max-employees', formControlName: 'employeesWithinScope'},
			{msg: 'validations.max-employees', formControlName: 'employeesCount'}
		],
		min: [
			{msg: 'validations.min-employees', formControlName: 'employeesWithinScope'},
			{msg: 'validations.min-employees', formControlName: 'employeesCount'}
		]
	};

	private allScopes: Scope[] = [];
	private controlNamesInOrder: string[] = [];

	protected buildFormFor(formKey: string, [scopes, product]: [Scope[], Product]) {
		this.allScopes = scopes;

		const form = new FormGroup({});
		this.formControlHandlers.forEach(handler => {
			if (handler.shouldApply()) {
				handler.buildControls(form, product, {allScopes: this.allScopes});
			}
		});
		this.initControlNamesWithOrder(product);
		return form;
	}

	getControlNamesInOrder(): string[] {
		return this.controlNamesInOrder;
	}

	fillOrder(order: Order): Order {
		this.formControlHandlers.forEach(handler => {
			if (handler.shouldApply()) {
				handler.fillOrder(this, order);
			}
		});
		return order;
	}

	protected override getFormSpecificErrorMap(): { [p: string]: ValidationMsg[] } {
		return this.FORM_CONTROL_ERROR_MSG_MAP;
	}

	protected override tryCopyAnswer(controlName: string, srcForm: string, dstForm: string): void {
		this.formControlHandlers.forEach(handler => handler.handleSpecificCopying(this, controlName, srcForm, dstForm));

		if (!['shiftsNumberMore', 'companyScopes'].includes(controlName)) {
			super.tryCopyAnswer(controlName, srcForm, dstForm);
		}
	}

	override destroySubForm(formKey: string) {
		super.destroySubForm(formKey);
		this.formControlHandlers.forEach(handler =>
			handler.getSubscriptions(formKey).forEach(sub => sub.unsubscribe())
		);
	}

	getScopeQuestionControlNamePrefix(scope: Scope, questionIndex: number): string {
		return 'scope-question-'.concat(scope.code).concat('-').concat(scope.questions[questionIndex].type.toLowerCase());
	}

	private initControlNamesWithOrder(product: Product) {
		const scopeQuestionsNames = List(this.allScopes).filter(scope => scope.standardName == product.standard.name)
			.filter(scope => scope.questions != null && scope.questions.length > 0).flatMap(scope => {
				return scope.questions.map((question, index) => this.getScopeQuestionControlNamePrefix(scope, index) + product.standard.name);
			}).toArray();

		this.controlNamesInOrder = this.controlNamesInOrder.concat(['companyDescription' + product.standard.name, 'employeesCount' + product.standard.name,
			'scopeDescriptionNeedsNarrowing' + product.standard.name, 'scopeDescription' + product.standard.name, 'companyScopes' + product.standard.name]).concat(scopeQuestionsNames)
			.concat(['employeesWithinScope' + product.standard.name, 'catastrophicRisk' + product.standard.name, 'partTimeEmployeesIncluded' + product.standard.name,
				'employeesCountDescription' + product.standard.name, 'shiftsNumber' + product.standard.name, 'shiftsNumberMore' + product.standard.name]);
	}
}

